// src/components/UI/AnimalProfile.tsx
import { isFulfilled } from "@reduxjs/toolkit";
import { useLoader } from "contexts/LoaderContext";
import { useDeviceType } from "hooks/useDeviceType";
import React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import { getPetOutputForAgent } from "services/AgentSaveHandler";
import { isAgent, isLandlord, isTenant, saveTenantPetData, updateTenantItemByUid } from "services/TenantSubmitHandler";
import { useAppDispatch, useAppSelector } from "store/configureStore";
import { setTenantItems } from "store/tenant/slice";
import { savePetDecisionByAgent } from "store/tenant/thunks";
import { TenantPetsData, TenantPetsDocument } from "store/tenant/types";
import { renumberFieldsByAnimalType, toggleClassOnElements } from "utils/childrenProcessor";
import { isQldRegion } from "utils/petFormHelpers";
import { collectFieldNames } from "utils/renderTemplates";
import { transformAnimalFormData } from "utils/splitDot";
import { IconName } from "../AnimalCard/index";
import Badge from "../UI/Badge";
import Button from "../UI/Button";
import Icon from "../UI/Icon/Icon";

/**
 * Props for the AnimalProfile component.
 *
 * @property {string} title - The title to display for the animal profile.
 * @property {IconName} type - The icon type representing the animal.
 * @property {() => void} [onRemove] - Optional callback invoked when the profile is removed.
 * @property {React.ReactNode} children - The content to render inside the profile.
 */
export interface AnimalProfileProps {
  petId?: string | null;
  type: IconName;
  title: string;
  onRemove: (profileMainTitle: string, profileSubHeaderText: string, uid: string) => void;
  animalType: IconName;
  children?: React.ReactNode;
  petData?: TenantPetsData[];
  petDocumentData?: TenantPetsDocument[];
  roleType?: string;
  collapse?: boolean;
  onOpen?: () => void;
  activeState?: number;
  approvalStatus?: string;
  conditionStatus?: string;
  keyIndex?: number;
  handleViewProfile?: (
    type: string,
    idx: number,
    petData: any,
    petDocuments: any[]
  ) => void;
  isModal?: boolean;
  profileHeaderText?: string;
  unique_link?: string;
  tenant_link?: string;
  animal_profile_id?: string;
  onProfileSaved?: (animal_profile_id: number) => void;
  uid?: number;
  maxHight?: boolean;
  /**
   * Optional callback to refresh tenant details after saving pet data.
   * @returns void
   */
  onRefreshAfterSavePet?: () => void;
}

/**
 * AnimalProfile component displays a profile card for an animal, including its title, type, and status.
 *
 * @param {AnimalProfileProps} props - The properties for the AnimalProfile component.
 * @param {string} props.title - The title or name of the animal profile.
 * @param {string} props.type - The type of animal, used to determine the icon.
 * @param {() => void} props.onRemove - Callback function triggered when the remove button is clicked.
 * @param {React.ReactNode} props.children - Child components or elements to be rendered within the profile card.
 *
 * @returns {JSX.Element} The rendered animal profile card component.
 *
 * @remarks
 * - The profile card can be expanded or collapsed by clicking the chevron icon.
 * - Displays a badge indicating the profile status and a button to remove the profile.
 */
const AnimalProfile: React.FC<AnimalProfileProps> = ({
  petId,
  title,
  type,
  onRemove,
  children,
  animalType,
  petData,
  petDocumentData,
  roleType,
  collapse,
  onOpen,
  activeState,
  keyIndex,
  approvalStatus,
  conditionStatus,
  handleViewProfile,
  isModal = "false",
  profileHeaderText,
  unique_link = "",
  tenant_link = "",
  animal_profile_id = "",
  onProfileSaved,
  uid,
  onRefreshAfterSavePet,
  maxHight
}) => {
  // Use collapse prop to control open/close
  const isOpen = !collapse;
  
  const disptach = useAppDispatch()
  const { showLoader, hideLoader } = useLoader();
  const uploadedDocumentsList = useAppSelector((state) => state.formTemplate.uploadedDocuments);
  const tenantItems = useAppSelector((state) => state.Tenant.tenantItems);
  const deviceType = useDeviceType();
  const formContext = useFormContext();
  // Only tenants consume the per-profile completeness result (`isComplete`): the
  // Complete/Incomplete badge and the Save Profile button. Agents/landlords get their
  // status from props (approvalStatus/conditionStatus), so for them we skip the whole
  // completeness pipeline below. This matters for performance: collecting every field
  // name, subscribing useWatch to all of them, and re-running getValues()/validation on
  // each render is expensive on the larger QLD agent form and made toggling
  // "I consent"/"I do not consent" lag worse with each click.
  const isTenantRole = isTenant();
  const requiredFields = isTenantRole ? collectFieldNames(children) : [];
  const watchNames: string[] = Array.isArray(requiredFields) ? requiredFields : [];
  const region = localStorage.getItem("pet_app_region") || "nsw"; // Default to NSW if region is not set

  // Watch the required fields to ensure component re-renders when form values change
  // This ensures validation runs with correct data after form populates.
  // For agent/landlord, watchNames is empty so this subscribes to nothing (no re-render churn).
  // Note: We must capture the return value to trigger re-renders, even if unused
  const watchedValues = useWatch({
    control: formContext?.control as any,
    name: watchNames as any,
    defaultValue: [] as any
  });
  // Use watchedValues to suppress unused variable warning and ensure re-renders work
  void watchedValues;

  if (!formContext) {
    return null; // or render a fallback UI
  }
  const { watch, formState, trigger, getValues } = formContext;
  const { errors } = formState;

  // Utility to get nested value by path string (e.g., 'dog.is_more_than_one.0')
  function getValueByPath(obj: any, path: string) {
    // Use optional chaining for concise nested access
    return path.split(".").reduce((acc, part) => acc?.[part], obj);
  }

    // Set the profile header text label based on user role and animal count
  let profileHeaderTextLabel = ""; 

  if (isAgent() || isLandlord()) {
    // For agent/landlord, use petData to show total animal count
    const count = petData?.[0]?.animal_count ?? 1;
    // Empty label for dog or cat, otherwise show count
    if (type === "dog" || type === "cat") {
      profileHeaderTextLabel = "";
    } else {
      profileHeaderTextLabel = `Profile applies to ${count} ${(String(type) === "smallMammal") ? "small mammal" : (String(type) === "other") ? "animal" : type}${count > 1 && String(type) !== "fish" ? "s" : ""} in total`;
    }
  } else {
    // For tenant, get count and pluralize if needed
    // QLD has no amount selector so always hide this text
    if (isQldRegion()) {
      profileHeaderTextLabel = "";
    } else {
      const isMultiple = watch(`${type}.is_more_than_one.${keyIndex}`, false);
      const count = isMultiple ? watch(`${type}.animal_count.${keyIndex}`, 2) : 1;
      if (type === "dog" || type === "cat") {
        profileHeaderTextLabel = "";
      } else {
        profileHeaderTextLabel =
          profileHeaderText ||
          `Profile applies to ${count} ${(String(type) === "smallMammal") ? "small mammal" : (String(type) === "other") ? "animal" : type}${count > 1 && String(type) !== "fish" ? "s" : ""} in total`;
      }
    }
  }
  
  let profileType = "";
  if(isAgent() || isLandlord()){
    const customType = petData?.[0]?.custom_type ?? "";
    profileType = isQldRegion() && !customType ? title : (title + ": " + customType);
  } else {
    const customType = watch(`${type}.type_of_animal.${keyIndex}`) || petData?.[0]?.custom_type || "";
    profileType = isQldRegion() && !customType ? title : (title + ": " + customType);
  }
    




    // Check if THIS profile is complete by checking only its fields
    // We can't use formState.isValid because that checks the entire form across all profiles
    // We need to check both: 1) if fields have errors, 2) if required fields are filled
    let isComplete = true;

    // Skip the (expensive) completeness computation entirely for agent/landlord — the
    // result is only rendered for tenants. See note above `isTenantRole`.
    if (isTenantRole) {
    const allFields = getValues() || {};

    // CRITICAL: Check if there are validation errors for THIS profile only
    // This includes dynamic fields like desc-{id}-{index} from UploadDocument that belong to this pet
    let hasProfileErrors = false;

    if (errors && Object.keys(errors).length > 0) {
      Object.keys(errors).forEach(errorKey => {
        // Check if this error belongs to this profile's required fields
        if (requiredFields.includes(errorKey)) {
          hasProfileErrors = true;
        }

        // Check if this error is a description field (desc-{id}-{index}) for THIS pet
        if (errorKey.startsWith('desc-') && typeof keyIndex === 'number') {
          // Get documents for this specific pet type and position
          const petDocs = uploadedDocumentsList?.filter(
            doc => doc.pet_type === type && doc.pet_index === keyIndex + 1
          );
          // Check if any of this pet's document IDs are in the error key
          const hasMatchingDoc = petDocs?.some(doc => errorKey.includes(doc.document_id));
          if (hasMatchingDoc) {
            hasProfileErrors = true;
          }
        }
      });
    }

    if (hasProfileErrors) {
      isComplete = false;
    }

    if (requiredFields.length > 0) {
      // Check for required fields and errors in THIS profile only
      const microchipIdx = requiredFields.findIndex(f => f.includes("microchipNumber"));
      // Check for both NSW (choices) and QLD (registration_number) field names
      let registrationIdx = requiredFields.findIndex(f => f.includes("choices"));
      if (registrationIdx === -1) {
        registrationIdx = requiredFields.findIndex(f => f.includes("registration_number"));
      }

      if (microchipIdx !== -1 && registrationIdx !== -1) {
        // Special case: at least one of microchip/registration must be filled
        // UNLESS "I do not have this pet yet" is selected (dont_have_reason = true)
        const dontHaveReasonIdx = requiredFields.findIndex(f => f.includes("dont_have_reason"));
        const dontHaveReasonValue = dontHaveReasonIdx !== -1 ? getValueByPath(allFields, requiredFields[dontHaveReasonIdx]) : false;

        const microchipValue = getValueByPath(allFields, requiredFields[microchipIdx]);
        const registrationValue = getValueByPath(allFields, requiredFields[registrationIdx]);
        const microchipError = getValueByPath(errors, requiredFields[microchipIdx]);
        const registrationError = getValueByPath(errors, requiredFields[registrationIdx]);

        const atLeastOneFilled = (microchipValue !== undefined && microchipValue !== null && microchipValue !== "") ||
                                 (registrationValue !== undefined && registrationValue !== null && registrationValue !== "");

        // Only require microchip/registration if user has the pet (dont_have_reason = false)
        if (!dontHaveReasonValue && !atLeastOneFilled) {
          isComplete = false;
        }

        // Check if microchip or registration fields have validation errors
        if (microchipError || registrationError) {
          isComplete = false;
        }

        // Check other fields - must have value AND no errors
        requiredFields.forEach((fieldPath, idx) => {
          if (idx === microchipIdx || idx === registrationIdx || idx === dontHaveReasonIdx) return;

          const fieldValue = getValueByPath(allFields, fieldPath);
          const fieldError = getValueByPath(errors, fieldPath);
          const fieldName = fieldPath.toLowerCase();
          const isEmpty = fieldValue === undefined || fieldValue === null || fieldValue === "" ||
            (fieldName.includes('tenant_understanding') && !fieldValue);

          // Field is incomplete if it has any validation error
          if (fieldError) {
            isComplete = false;
          }

          // Field is incomplete if it's empty (since it's in requiredFields list)
          // EXCEPT if it's a known optional field
          // We identify optional fields by checking if they contain "optional", "supporting", or "reason" in the name
          // This is based on the JSON structure where optional fields are named descriptively

          // Special case: reason_for is CONDITIONALLY required
          // - If exemption (registration) is selected → reason_for is REQUIRED
          // - If microchip is filled → reason_for is optional
          const isReasonForField = fieldName.includes('reason_for');
          const exemptionSelected = registrationValue !== undefined && registrationValue !== null && registrationValue !== "";

          const isKnownOptionalField =
            fieldName.includes('supporting_information') ||
            fieldName.includes('supporting') ||
            fieldName.includes('optional') ||
            fieldName.includes('description2') ||  // Supporting Information field for dog/cat
            (!isQldRegion() && fieldName.includes('fur_colour_length')) ||  // QLD: required for all pets; NSW unchanged (optional)
            (!isQldRegion() && fieldName.includes('weight_height_length')) ||
            (isQldRegion() && fieldName.includes('.description.')) ||  // QLD-only optional "other information"; NSW 'description' is required
            (isReasonForField && !exemptionSelected);  // reason_for is optional ONLY when exemption is NOT selected

          if (isEmpty && !isKnownOptionalField) {
            isComplete = false;
          }
        });
      } else {
        // Normal case (no microchip/choices): check if fields have errors OR are empty
        let hasAnyValue = false;
        let hasAnyError = false;

        requiredFields.forEach(fieldPath => {
          const fieldValue = getValueByPath(allFields, fieldPath);
          const fieldError = getValueByPath(errors, fieldPath);
          const fieldName = fieldPath.toLowerCase();
          const isEmpty = fieldValue === undefined || fieldValue === null || fieldValue === "" ||
            (fieldName.includes('tenant_understanding') && !fieldValue);

          // Track if any field has a value
          if (!isEmpty) {
            hasAnyValue = true;
          }

          // Field is incomplete if it has any validation error
          if (fieldError) {
            hasAnyError = true;
            isComplete = false;
          }

          // Field is incomplete if it's empty EXCEPT if it's a known optional field
          const isKnownOptionalField =
            fieldName.includes('supporting_information') ||
            fieldName.includes('supporting') ||
            fieldName.includes('optional') ||
            fieldName.includes('description2') ||  // Supporting Information field for dog/cat
            (!isQldRegion() && fieldName.includes('fur_colour_length')) ||  // QLD: required for all pets; NSW unchanged (optional)
            (!isQldRegion() && fieldName.includes('weight_height_length')) ||
            (isQldRegion() && fieldName.includes('.description.')) ||  // QLD-only optional "other information"; NSW 'description' is required
            fieldName.includes('reason_for');

          if (isEmpty && !isKnownOptionalField) {
            isComplete = false;
          }
        });

        // If ALL fields are empty and no errors, it's a new untouched profile - mark incomplete
        if (!hasAnyValue && !hasAnyError) {
          isComplete = false;
        }
      }
    }
    } // end if (isTenantRole)

    const profileMainTitle = type !== "dog" && type !== "cat" && type !== "fish" && profileType ? profileType : title;
    const profileSubHeaderText = profileHeaderTextLabel ?? "";
  
  

  const handleSaveProfile = async () => {
    /* eslint-disable no-console */
    console.log("[SaveProfile] CLICKED — uid:", uid, "type:", type, "keyIndex:", keyIndex);
    showLoader();
    const requiredFields = collectFieldNames(children);

    // Include desc-* fields for THIS pet's uploaded documents only
    // (avoids validating other profiles' desc fields)
    const petDescFields = (uploadedDocumentsList ?? [])
      .filter(doc => doc.pet_type === type && doc.pet_index === (keyIndex ?? 0) + 1)
      .map(doc => `desc-${doc.document_id}`);

    const fieldsToValidate = [...requiredFields, ...petDescFields];
    console.log("[SaveProfile] fieldsToValidate:", fieldsToValidate);

    // Trigger validation only on THIS profile's fields to avoid
    // validation errors from other (empty) pet profiles blocking the save
    const isValid = await trigger(fieldsToValidate as any);
    console.log("[SaveProfile] isValid (trigger result):", isValid);

    // Check errors only for THIS profile's fields
    const hasErrors = fieldsToValidate.some(field => {
      const error = field.split('.').reduce((acc: any, part) => acc?.[part], formState.errors);
      return !!error;
    });
    console.log("[SaveProfile] hasErrors:", hasErrors, "| formState.errors keys:", Object.keys(formState.errors));
    console.log("[SaveProfile] will dispatch?:", isValid && !hasErrors);
    /* eslint-enable no-console */

    const allValues = getValues();
    const profileData = requiredFields.reduce<Record<string, any>>(
      (acc, field) => {
        acc[field] = getValueByPath(allValues, field);
        return acc;
      },
      {}
    );
    const payload: Record<string, any> = {};
    const requiredFormat: Record<string, any> = transformAnimalFormData(
      profileData,
      uploadedDocumentsList,
      uid,
      tenantItems
    );

    payload["pets"] = requiredFormat || [];
    payload["unique_link_token"] = tenant_link || "";
    payload["tenant_link"] = unique_link || "";

    if (isValid && !hasErrors) {
      const savePetResponse = await disptach(
        saveTenantPetData(payload, unique_link)
      );
      if (onProfileSaved && typeof uid === "number") {
        onProfileSaved(uid);
      }

      if (isFulfilled(savePetResponse)) {
        if (typeof uid === "number") {
          const updatedItems = updateTenantItemByUid(tenantItems ?? [], uid, {
            pet_id: savePetResponse?.payload?.data?.latest_pet_id,
          });
          await disptach(setTenantItems(updatedItems));
        }
        toast.success("Profile saved successfully");
      }
      // Callback to refresh tenant details in parent component
      onRefreshAfterSavePet?.();

      if (onOpen) {
        onOpen();
      }
    }
    hideLoader();
  };
  
  const handleSavePetDecision = async() => {
    showLoader();
    const requiredFields = collectFieldNames(children);

    // `requiredFields` only contains the fields that are currently VISIBLE: renderTemplates
    // omits any field whose `showIf` condition isn't met. On the QLD agent consent form the
    // Condition/Reason textareas are shown only under "I consent" + "Yes (conditions)". When the
    // agent switches to "I do not consent" those fields hide but stay registered with their
    // `required` rule, so a blanket trigger()/full-form error check wrongly blocked Save.
    // For QLD, validate (and count errors for) only the visible fields. Other regions keep the
    // original full-form validation untouched so nothing else changes.
    const isQld = isQldRegion();
    const isValid = isQld ? await trigger(requiredFields as any) : await trigger();

    // Double-check: ensure no errors exist. For QLD, only consider the visible/validated fields
    // (a hidden Condition/Reason may still hold a stale error that must not block the save).
    const hasErrors = isQld
      ? requiredFields.some(field => !!field.split('.').reduce((acc: any, part) => acc?.[part], formState.errors))
      : (formState.errors && Object.keys(formState.errors).length > 0);

    const allValues = getValues();
    const profileData = requiredFields.reduce<Record<string, any>>((acc, field) => {
      acc[field] = getValueByPath(allValues, field);
      return acc;
    }, {});

    const petRequiredFormat = getPetOutputForAgent(profileData, petId ?? "");
    const payload: Record<string, any> = {};
    payload.pets = [petRequiredFormat];

    const urlParts = window.location.pathname.split("/");
    const agentId = urlParts?.[2];
    const agentLink = urlParts?.[4];

    if(isValid && !hasErrors){
      try {
        showLoader();
        const response = await disptach(savePetDecisionByAgent({ agentId, applicationId: agentLink, agentResponseData: payload }));
        
        if(isFulfilled(response)) {
          toast.success("Profile saved successfully");
        }
      } catch (error) {
        // Error saving pet decision
      }

      if (onOpen) {
        onOpen();
      }
    }
    
    
    
    hideLoader();
  }

  // Determine if flk-agent-line elements should be hidden
  const shouldHideAgentLine = (isAgent() || isLandlord()) && (!petDocumentData || petDocumentData.length === 0);

  // Process children to conditionally show/hide flk-agent-line elements
  let processedChildren = (isAgent() || isLandlord())
    ? toggleClassOnElements(children, 'flk-agent-line', 'd-none', shouldHideAgentLine)
    : children;
  
  // Renumber fields for fish and other animals
  processedChildren = renumberFieldsByAnimalType(processedChildren, type);

  return (
    <div
      className={`border border-grey-3 p-20px radius-5px drop-shadow d-flex flex-column gap-30px modal-body ${
        maxHight && "max-h-70vh overflow-auto"
      }`}
    >
      <div
        className="bg-light-blue p-10px radius-5px min-h-md-70px d-flex flex-column flex-md-row gap-10px gap-md-30px align-items-md-center cursor-pointer"
        onClick={() => {
          if (onOpen) {
            onOpen();
          }
        }}
        role="button"
        tabIndex={0}
      >
        <div className="d-flex gap-10px gap-md-30px align-items-center m-w-400px overflow-hidden">
          {(isModal === "false" || isModal === false) && activeState !== 1 && (
            <div className="d-flex text-grey-2 hover:text-black gap-8px align-items-center w-auto w-md-100px cursor-pointer">
              <Icon name={isOpen ? "chevronUp" : "chevronDown"} />
              <div className="fs-16px fw-bold  d-none d-md-block">
                {isOpen ? "Collapse" : "Expand"}
              </div>
            </div>
          )}
          <div className="d-flex gap-10px align-items-center m-w-400px overflow-hidden">
            {/* Show icon if isModal is true for both mobile and desktop */}
            {isModal === true && deviceType === "mobile" && (
              <Icon name={type} size={40} className="min-w-40px " />
            )}

            {deviceType !== "mobile" && (
              <Icon name={type} size={40} className="min-w-40px " />
            )}

            <div className="d-flex flex-column">
              <div className="fs-18px fw-bold text-black text-break">
                {profileMainTitle}
              </div>
              <div className="fs-14px fw-normal text-black">
                {" "}
                {region !== "qld" && profileSubHeaderText}{" "}
              </div>
            </div>
          </div>
        </div>
        {(isAgent() || isLandlord()) && activeState !== 1 && (
          <div className="d-block d-md-none">
            <Button
              type="button"
              className="btn-theme text-black hover:text-black  bg-medium-blue"
              iconName="attachment"
              // iconColor="grey-2"
              text={"View Pet Info"}
              onClick={(e) => {
                e.stopPropagation();
                handleViewProfile?.(
                  animalType,
                  keyIndex || 0,
                  petData && petData.length > 0 ? petData[0] : null,
                  petDocumentData ?? []
                );
              }}
            />
          </div>
        )}
        <div className="bg-lines min-h-1px d-block d-md-none"></div>

        <div className="d-flex flex-wrap gap-10px align-items-md-center flex-fill justify-content-center">
          {isTenant() && (
            <Badge color={isComplete ? "blue" : "grey-1"}>
              {isComplete ? "Complete" : "Incomplete"}
            </Badge>
          )}

          {(roleType === "agent" || roleType === "landlord") &&
            activeState !== 1 && (
              <>
                {approvalStatus === "approved" && (
                  <Badge color="navy">Approved</Badge>
                )}
                {conditionStatus === "no-condition" && region !== "qld" && (
                  <Badge color="grey-2">No Conditions</Badge>
                )}
                {conditionStatus === "condition-apply" && (
                  <Badge color="blue">Conditions Apply</Badge>
                )}
                {approvalStatus === "declined" && (
                  <Badge color="red">Declined</Badge>
                )}
                {approvalStatus === "incomplete" && (
                  <Badge color="grey-1">Incomplete</Badge>
                )}
              </>
            )}

          {isTenant() && (
            <Button
              type="button"
              className="btn-theme px-0 h-auto text-grey-2 hover:text-black ms-auto bg-transparent"
              iconName="remove"
              // iconColor="grey-2"
              text="Remove"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(
                  profileMainTitle,
                  profileSubHeaderText,
                  String(uid ?? "")
                );
              }}
            />
          )}

          {(isAgent() || isLandlord()) && activeState !== 1 && (
            <div className="d-none d-md-block ms-auto">
              <Button
                type="button"
                className="btn-theme text-black hover:text-black  bg-medium-blue"
                iconName="attachment"
                // iconColor="grey-2"
                text={"View Pet Info"}
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewProfile?.(
                    animalType,
                    keyIndex || 0,
                    petData && petData.length > 0 ? petData[0] : null,
                    petDocumentData ?? []
                  );
                }}
              />
            </div>
          )}
        </div>

        {(isModal === "false" || isModal === false) &&
          isAgent() &&
          activeState === 1 && (
            <div className="d-flex text-grey-2 hover:text-black gap-8px align-items-center w-auto w-md-100px cursor-pointer ms-auto">
              <Icon name={isOpen ? "chevronUp" : "chevronDown"} />
              <div className="fs-16px fw-bold ">
                {isOpen ? "Collapse Pet Info" : "Expand Pet Info"}
              </div>
              <div className=" text-grey-2">
                {isAgent() && activeState === 1 && petDocumentData && petDocumentData.length > 0 && (
                  <>
                    <Icon name="attachment" size={20} color="#495054" />{" "}
                    {`${petDocumentData.length} Files`}
                  </>
                )}
              </div>
            </div>
          )}
      </div>
      {<div className={isOpen ? "d-contents" : "d-none"}>{processedChildren}</div>}
      {isOpen && isTenant() && (
        <div className="d-flex flex-column flex-md-row gap-20px justify-content-md-end">
          <Button
            type="button"
            className="btn-theme bg-grey-1 text-white hover:bg-grey-2 disabled:bg-disabled"
            iconName="check"
            iconPosition="left"
            // iconColor="grey-2"
            text="Save Profile"
            disabled={!isComplete}
            onClick={handleSaveProfile}
          />
        </div>
      )}

      {isOpen && isAgent() && activeState !== 1 && (
        <div className="d-flex flex-column flex-md-row gap-20px justify-content-md-end">
          <Button
            type="button"
            className="btn-theme bg-grey-1 text-white hover:bg-grey-2 disabled:bg-disabled"
            iconName="check"
            iconPosition="left"
            // iconColor="grey-2"
            text="Save"
            onClick={handleSavePetDecision}
          />
        </div>
      )}
    </div>
  );
};

export default AnimalProfile;
